/** Reads an inclusive byte range of a file. Injected rather than imported so
 *  the parsers have no Drive dependency and can be tested against a buffer. */
export type RangeReader = (start: number, end: number) => Promise<ArrayBuffer>;

const BLOCK = 64 * 1024;

/**
 * Random access to a remote file, fetched in 64 KB blocks that are kept.
 *
 * Metadata parsers read many tiny headers close together. Fetching a block at
 * a time turns hundreds of 8-byte reads into a handful of requests, while the
 * regions a parser skips — the audio itself, or the multi-megabyte sample
 * tables of a long recording — are never downloaded at all.
 */
export class BlockReader {
  private readonly blocks = new Map<number, Promise<Uint8Array>>();
  private readonly read: RangeReader;
  readonly size: number;

  constructor(read: RangeReader, size: number) {
    this.read = read;
    this.size = size;
  }

  /** Up to `length` bytes from `offset`; shorter at the end of the file. */
  async bytes(offset: number, length: number): Promise<Uint8Array> {
    const end = Math.min(this.size, offset + length);
    if (offset < 0 || offset >= end) return new Uint8Array(0);

    const first = Math.floor(offset / BLOCK);
    const last = Math.floor((end - 1) / BLOCK);

    // Fetch each run of missing blocks with a single request.
    let runStart: number | null = null;
    for (let b = first; b <= last + 1; b++) {
      const missing = b <= last && !this.blocks.has(b);
      if (missing && runStart === null) runStart = b;
      if (!missing && runStart !== null) {
        this.fetchRun(runStart, b - 1);
        runStart = null;
      }
    }

    const out = new Uint8Array(end - offset);
    let written = 0;
    for (let b = first; b <= last; b++) {
      const block = await this.blocks.get(b);
      if (!block) break;
      const blockStart = b * BLOCK;
      const from = Math.max(offset, blockStart) - blockStart;
      const to = Math.min(end - blockStart, block.length);
      if (to <= from) break;
      out.set(block.subarray(from, to), written);
      written += to - from;
    }
    return written === out.length ? out : out.subarray(0, written);
  }

  /** Bytes read with no block rounding and no caching — for small reads
   *  scattered through a large file, where whole blocks would be waste. */
  async exact(offset: number, length: number): Promise<Uint8Array> {
    const end = Math.min(this.size, offset + length);
    if (offset < 0 || offset >= end) return new Uint8Array(0);
    return new Uint8Array(await this.read(offset, end - 1));
  }

  private fetchRun(first: number, last: number): void {
    const start = first * BLOCK;
    const end = Math.min(this.size, (last + 1) * BLOCK) - 1;
    const whole = this.read(start, end).then((buffer) => new Uint8Array(buffer));
    for (let b = first; b <= last; b++) {
      const at = (b - first) * BLOCK;
      this.blocks.set(
        b,
        whole.then((all) => all.subarray(at, at + BLOCK)),
      );
    }
  }
}

/* ---------- small decoding helpers shared by the parsers ---------- */

export function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/** Four (or n) single-byte characters, e.g. a box type or frame id. */
export function ascii(bytes: Uint8Array, at: number, length = 4): string {
  let out = '';
  for (let i = at; i < at + length && i < bytes.length; i++) out += String.fromCharCode(bytes[i] ?? 0);
  return out;
}

const decoders: Record<string, TextDecoder> = {};
export function decodeText(bytes: Uint8Array, encoding = 'utf-8'): string {
  decoders[encoding] ??= new TextDecoder(encoding);
  return decoders[encoding].decode(bytes).replace(/\0+$/, '').trim();
}

/** Picture bytes found inside an audio file. */
export interface EmbeddedPicture {
  mimeType: string;
  data: Uint8Array;
}

/** Image type from its first bytes, for formats that do not label it. */
export function sniffImageType(data: Uint8Array): string {
  if (data[0] === 0xff && data[1] === 0xd8) return 'image/jpeg';
  if (data[0] === 0x89 && ascii(data, 1, 3) === 'PNG') return 'image/png';
  if (ascii(data, 0, 3) === 'GIF') return 'image/gif';
  if (ascii(data, 0, 4) === 'RIFF' && ascii(data, 8, 4) === 'WEBP') return 'image/webp';
  if (ascii(data, 0, 2) === 'BM') return 'image/bmp';
  return 'application/octet-stream';
}
